import { Injectable } from '@angular/core';
import { clientUserDictionaryEntry } from '@capiche/ts-data-models-client';
import { TsTrie } from '@capiche/ts-trie';
import { Subject } from 'rxjs';
import { SuggestionRepository } from '../data-access-layer/suggestionRepository';
import { UserService } from 'capiche-auth';
import { tokenDTO, suggestionDTO, spellingStateEnum, editStateEnum } from '@capiche/ts-data-models-shared';
import { CapicheLoggerService } from 'capiche-logger';

// split dictionary entry into key and value types for storage in the trie
type DictionaryKeyField = 'lookupWord';
type DictionaryKey = clientUserDictionaryEntry[DictionaryKeyField];
type DictionaryValue = Omit<clientUserDictionaryEntry, DictionaryKeyField>;

@Injectable({
  providedIn: 'root',
})
/**
 * @class DictionaryService
 * On the client side the dictionary is a ts-trie keyed by lookupWord.
 * We store only DictionaryValue[] at each node and re-attach the key when returning data.
 */
export class DictionaryService {
  private logger: CapicheLoggerService = new CapicheLoggerService('dictionary.service:', false);

  private dictionary: TsTrie<DictionaryValue> = new TsTrie();

  public dictionaryUpdated$: Subject<void> = new Subject<void>();

  constructor(
    private suggestionRepository: SuggestionRepository,
    private userService: UserService,
  ) {
    this.userService.currentUser$.subscribe(async (user) => {
      this.logger.log('this.user.uid: ', user?.uid);

      if (!user) {
        this.logger.log('no user, clearing dictionary');
        this.dictionary = new TsTrie();
        return;
      }

      // get the words the user has previously used
      const response: clientUserDictionaryEntry[] = await this.suggestionRepository.getAllStoredLookups();
      // build an initial dictionary from the user's stored lookups
      this.logger.log(`fetched ${response.length} stored lookups for user ${user.uid}`);

      const groups = {
        unchecked: response.filter((e) => e.spellingState === spellingStateEnum.unchecked),
        correct: response.filter((e) => e.spellingState === spellingStateEnum.correct),
        contextual: response.filter((e) => e.spellingState === spellingStateEnum.contextual),
        misspelled: response.filter((e) => e.spellingState === spellingStateEnum.misspelled),
        user: response.filter((e) => e.spellingState === spellingStateEnum.user),
        unknown: response.filter((e) => e.spellingState === spellingStateEnum.unknown),
        recovered: response.filter((e) => e.spellingState === spellingStateEnum.recovered),
        ignore: response.filter((e) => e.spellingState === spellingStateEnum.ignore),
      };

      this.logger.log(
        'stored lookups by state: ' +
          `unchecked=${groups.unchecked.length} ` +
          `correct=${groups.correct.length} ` +
          `contextual=${groups.contextual.length} ` +
          `misspelled=${groups.misspelled.length} ` +
          `user=${groups.user.length} ` +
          `unknown=${groups.unknown.length} ` +
          `recovered=${groups.recovered.length} ` +
          `ignore=${groups.ignore.length} `,
      );

      // log the words in each group (truncate long lists for readability)
      Object.entries(groups).forEach(([state, entries]) => {
        if (entries.length > 0) {
          const words = entries.map((e) => e.lookupWord).join(', ');
          this.logger.log(`- ${state} (${entries.length}): ${words}`);
        }
      });

      this.addEntries(response);
    });
  }

  /**
   * Returns dictionary entries for the provided input or null if none found.
   * @param input
   */
  getEntry(input: DictionaryKey): clientUserDictionaryEntry | null {
    const values: DictionaryValue[] = this.dictionary.getAll(input) ?? [];

    // relies on the dictionary trie containing valid data for all fields
    const entries: clientUserDictionaryEntry[] = values.map((value) => ({
      lookupWord: input, // re-attach the key
      ...value,
    }));

    //    this.logger.log('getEntry:', entries);
    this.logger.log(
      'getEntry:',
      JSON.stringify(
        entries.map((e) => ({
          word: e.lookupWord,
          state: e.spellingState,
          suggestions: e.suggestions.length,
        })),
      ),
    );

    if (entries.length > 1)
      this.logger.error(`getEntry: multiple values stored for key=${input}, taking [0] only:`, entries);
    return entries[0] || null; // by design there should only be one entry per lookupWord TODO: remove the flatMap and just return the first value
  }

  /**
   * Adds the provided clientUserDictionaryEntry[] to the dictionary.
   */
  addEntries(input: clientUserDictionaryEntry[]): void {
    const t0 = performance.now();

    // filter out contextual entries and deduplicate by lookupWord
    const entries = Array.from(
      new Map(
        input
          .filter((entry) => entry.spellingState !== spellingStateEnum.contextual)
          .filter((entry) => entry.spellingState !== spellingStateEnum.ignore) // ignore special tokens (urls/emails etc) don't add to the trie/dictionary
          .map((entry) => [entry.lookupWord, entry]),
      ).values(),
    );

    entries.forEach((entry) => {
      const value: DictionaryValue = {
        spellingState: entry.spellingState,
        suggestions: entry.suggestions,
      };
      this.logger.log(
        'addEntries:',
        JSON.stringify({
          lookupWord: entry.lookupWord,
          state: entry.spellingState,
          suggestions: entry.suggestions?.length || 0,
        }),
      );
      // upsert is really just a put or set
      // - it replaces any existing value or adds a new one to the trie
      this.dictionary.upsert(entry.lookupWord, value);
    });

    this.logger.log(
      `addEntries: ${input.length} reduced to ${entries.length} unique entries added/updated in ${
        performance.now() - t0
      }ms dictionary size: ${this.dictionary.length()}`,
    );

    this.dictionaryUpdated$.next();
  }

  /**
   * Takes a tokenDTO[] and adds them to the dictionary.
   */
  tokenDTOaddEntries(tokensIn: tokenDTO[]): void {
    this.logger.log(`tokenDTOaddEntries: ${tokensIn.length} tokens`);

    const entries: clientUserDictionaryEntry[] = tokensIn
      .filter((token) => token.editState === editStateEnum.checked)
      .map((token) => ({
        lookupWord: token.input.toLocaleLowerCase(),
        // coerce contextual → correct (ensure contextual api responses are added to the trie as correct)
        // implemented in https://capiche.atlassian.net/browse/CSW-1232
        // this is possible because of the changes in https://capiche.atlassian.net/browse/CSW-1220
        //   'Server assigns contextual only to correctly spelled words 35e8c9'
        //
        // if contextual > coerce to > correct
        // otherwise use the provided spellingState
        // unless absent, then default to unchecked
        spellingState:
          token.spellingState === spellingStateEnum.contextual
            ? spellingStateEnum.correct
            : (token.spellingState ?? spellingStateEnum.unchecked),
        suggestions:
          token.suggestions?.map((s: suggestionDTO) => ({
            word: s.word || '',
            definition: s.definition || '',
            rank: s.rank,
          })) || [],
      }));

    this.addEntries(entries);
  }
}
